import {useMutation, useQueryClient} from '@tanstack/react-query';
import {updateUser} from '../endpoints/api';
import snakeCaseObj from '../utils/snakeCaseObj';
import useAuth, {User} from './useAuth';

/**
 * Centralized mutation hook for updating the current authenticated user's
 * profile. Provides optimistic in-memory updates so the UI reflects changes
 * instantly, with rollback on error and a non-blocking refetch on settle to
 * reconcile any server-side normalization (e.g. handle slugification).
 *
 * Pass a camelCase partial of the User object. Keys are converted to
 * snake_case before being sent to the API.
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const {user, patchUser, reFetchUserData} = useAuth();
  const handle = user?.handle;
  const userId = user?.id;

  return useMutation({
    mutationFn: (payload: Partial<User> & Record<string, any>) =>
      updateUser(snakeCaseObj(payload)),
    onMutate: async (payload) => {
      const previousUser = user;
      // Optimistically merge the camelCase partial into the auth user so the
      // UI reflects the change before the network round-trip completes.
      patchUser(payload);
      return {previousUser};
    },
    onError: (_err, _payload, context) => {
      // Restore the previous user snapshot if the request failed.
      if (context?.previousUser) {
        patchUser(context.previousUser);
      }
    },
    onSettled: () => {
      // Non-blocking refetch to reconcile any server-side transformations.
      reFetchUserData().catch(() => {});
      // Invalidate the user's own public profile cache so other surfaces
      // reflect the change without a manual refresh.
      if (handle) queryClient.invalidateQueries(['publicProfile', handle]);
      if (userId !== undefined) {
        queryClient.invalidateQueries(['publicProfile', String(userId)]);
      }
    },
  });
};
